/**
 * Favicon da imobiliaria no site e no portal (Feature FAVICON).
 *
 * O `index.html` traz os icones da Corretto (svg, ico, png, apple-touch-icon,
 * mask-icon). So trocar o `href` de um deles nao basta: o navegador escolhe
 * entre varios `rel="icon"` e o SVG da Corretto ganharia. Por isso os links de
 * icone saem do `<head>` (ficam marcadores no lugar) e entram os da
 * imobiliaria; ao sair, os originais voltam na mesma posicao.
 *
 * O ultimo favicon de cada imobiliaria fica no `localStorage` para o
 * `index.tsx` aplicar antes do React montar e evitar a piscada do icone da
 * Corretto (decisao D5).
 */

import { useEffect } from 'react'

import { getTenantSlug } from 'common/tenant'
import { getHostRole } from 'common/tenant/host'

const CACHE_PREFIX = 'corretto:favicon:'

// Rotas que ficam com o icone da Corretto (decisao D1) ou que nao montam o
// AgencyThemeProvider: nelas ninguem corrigiria um icone do cache velho
const PLATFORM_ROUTES = [
  'admin',
  'captador',
  'para-imobiliarias',
  'upload',
  'proposta',
  'sign'
]

interface RemovedIcon {
  link: Element
  marker: Comment
}

// Estado do modulo, e nao do hook: o `index.tsx` troca o icone antes do React
// montar e o hook assume depois, restaurando os originais da Corretto.
let removedIcons: RemovedIcon[] | null = null
let injectedLinks: HTMLLinkElement[] = []
let appliedFromCache = false

export function faviconType(url: string): string | undefined {
  const path = url.split(/[?#]/)[0].toLowerCase()
  if (path.endsWith('.svg')) return 'image/svg+xml'
  if (path.endsWith('.png')) return 'image/png'
  if (path.endsWith('.ico')) return 'image/x-icon'
  return undefined
}

function isIconLink(link: Element): boolean {
  return (link.getAttribute('rel') || '').toLowerCase().includes('icon')
}

/** Troca os icones do `<head>` pelo `url`. Pode ser chamada de novo. */
export function applyFavicon(url: string): void {
  const head = document.head

  if (!removedIcons) {
    removedIcons = Array.from(head.querySelectorAll('link'))
      .filter(isIconLink)
      .map((link) => {
        const marker = document.createComment('favicon')
        link.replaceWith(marker)
        return { link, marker }
      })
  }

  injectedLinks.forEach((link) => link.remove())

  const type = faviconType(url)
  const icon = document.createElement('link')
  icon.rel = 'icon'
  if (type) icon.type = type
  icon.href = url
  const touch = document.createElement('link')
  touch.rel = 'apple-touch-icon'
  touch.href = url
  injectedLinks = [icon, touch]

  const anchor = removedIcons[0]?.marker
  injectedLinks.forEach((link) =>
    anchor?.parentNode
      ? anchor.parentNode.insertBefore(link, anchor)
      : head.appendChild(link)
  )
  appliedFromCache = false
}

/** Tira os icones da imobiliaria e devolve os originais. */
export function restoreFavicon(): void {
  injectedLinks.forEach((link) => link.remove())
  injectedLinks = []
  removedIcons?.forEach(({ link, marker }) => marker.replaceWith(link))
  removedIcons = null
  appliedFromCache = false
}

/**
 * Chave do cache do endereco atual, ou `null` onde ele nao vale (hosts da
 * plataforma sem imobiliaria). No dominio proprio o slug so chega depois do
 * bootstrap, entao a chave e o host.
 */
export function faviconCacheKey(): string | null {
  if (getHostRole() === 'custom') {
    return `${CACHE_PREFIX}host:${window.location.hostname.toLowerCase()}`
  }
  const slug = getTenantSlug()
  return slug ? `${CACHE_PREFIX}${slug}` : null
}

function readCache(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeCache(key: string, url: string | null): void {
  try {
    if (url) localStorage.setItem(key, url)
    else localStorage.removeItem(key)
  } catch {
    // localStorage bloqueado: fica so sem o cache
  }
}

function isPlatformRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  // Com o slug no caminho (`/imobx/admin`) a rota e o segundo segmento
  const slug = getTenantSlug()
  if (slug && segments[0] === slug) segments.shift()
  return PLATFORM_ROUTES.includes(segments[0] || '')
}

/** Aplica o ultimo favicon salvo antes do React montar (decisao D5). */
export function applyCachedFavicon(): void {
  if (isPlatformRoute(window.location.pathname)) return
  const key = faviconCacheKey()
  const url = key && readCache(key)
  if (!url) return
  applyFavicon(url)
  appliedFromCache = true
}

/**
 * Mostra o favicon da imobiliaria enquanto montado.
 *
 * `url` `undefined` = agencia ainda carregando (nao mexe); `null` = sem
 * favicon (fica o da Corretto, decisao D3).
 */
export function useAgencyFavicon(
  url: string | null | undefined,
  enabled: boolean
): void {
  useEffect(() => {
    if (!enabled || url === undefined) return

    if (!url) {
      // O cache pode ter aplicado um favicon que a imobiliaria ja removeu
      if (appliedFromCache) restoreFavicon()
      const key = faviconCacheKey()
      if (key) writeCache(key, null)
      return
    }

    applyFavicon(url)
    const key = faviconCacheKey()
    if (key) writeCache(key, url)
    return () => restoreFavicon()
  }, [url, enabled])

  // Fora do site (backoffice, login da plataforma) o icone do cache nao vale
  useEffect(() => {
    if (!enabled && appliedFromCache) restoreFavicon()
  }, [enabled])
}
